import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FiatCurrency,
  TournamentWinner,
} from "@nestcoinco/hyper-api-gateway-api-client";

import ImageFallbackAvatar from "../../../images/fallback-avatar.png";
import useExchangeRatesQuery from "../../../react-query/queries/use-exchange-rates";
import useLatestWinnersQuery from "../../../react-query/queries/use-latest-winners";
import { cx } from "../../../utils/cx";
import {
  formatFiatWithAbbreviation,
  getTimeAgo,
} from "../../../utils/helpers";

const MIN_WINNER_CARDS_PER_ROW = 12;

interface DisplayedWinner {
  username: string;
  avatarUrl?: string;
  winText: string;
  winTime: string;
}

interface MarqueeWinner {
  winner: DisplayedWinner;
  isDuplicate: boolean;
}

function fillMarqueeRow(row: DisplayedWinner[]): MarqueeWinner[] {
  const filledRow: MarqueeWinner[] = [];

  while (filledRow.length < MIN_WINNER_CARDS_PER_ROW) {
    row.forEach((winner) => {
      filledRow.push({
        winner,
        isDuplicate: filledRow.length >= row.length,
      });
    });
  }

  return [
    ...filledRow,
    ...filledRow.map(({ winner }) => ({ winner, isDuplicate: true })),
  ];
}

interface WinnerCardProps extends DisplayedWinner {
  isDuplicate: boolean;
}

const WinnerCard = memo(function WinnerCard({
  username,
  avatarUrl,
  winText,
  winTime,
  isDuplicate,
}: WinnerCardProps) {
  return (
    <article
      className="winner-card"
      aria-hidden={isDuplicate || undefined}
    >
      <img
        src={avatarUrl || ImageFallbackAvatar}
        alt=""
        className="winner-avatar"
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = ImageFallbackAvatar;
        }}
      />
      <div className="winner-copy">
        <p className="winner-name">@{username}</p>
        <p className="winner-amount">{winText}</p>
        <p className="winner-time">{winTime}</p>
      </div>
    </article>
  );
});

export default function WinnersCarousel() {
  const { t } = useTranslation("games", {
    keyPrefix: "game_landing",
  });
  const { latestWinners } = useLatestWinnersQuery();
  const { getExchangeRate } = useExchangeRatesQuery();
  const nairaRate = getExchangeRate(FiatCurrency.NGN);

  const winners = useMemo(() => {
    const sortedWinners = [...(latestWinners?.records ?? [])].sort(
      (a, b) =>
        (b.tournament?.rewardAmount ?? 0) -
        (a.tournament?.rewardAmount ?? 0)
    );

    return sortedWinners.map(
      (winner: TournamentWinner): DisplayedWinner => {
        const rewardAmount = winner.tournament?.rewardAmount;
        const displayedRewardAmount =
          rewardAmount !== undefined && nairaRate !== undefined
            ? formatFiatWithAbbreviation(
                rewardAmount * nairaRate,
                FiatCurrency.NGN
              )
            : "--";

        return {
          username: winner.username,
          avatarUrl: winner.avatarUrl,
          winText: t("winner_won", { amount: displayedRewardAmount }),
          winTime: getTimeAgo(winner.tournament?.endDate),
        };
      }
    );
  }, [latestWinners?.records, nairaRate, t]);

  if (!winners.length) return null;

  const midpoint = Math.ceil(winners.length / 2);
  const firstRow = winners.slice(0, midpoint);
  const secondRow = winners.slice(midpoint);
  const rows = [
    { winners: firstRow, isRepeatedRow: false },
    {
      winners: secondRow.length ? secondRow : firstRow,
      isRepeatedRow: !secondRow.length,
    },
  ];

  return (
    <section className="winners">
      <h2>{t("players_winning_every_day")}</h2>
      <div className="winner-rows">
        {rows.map(({ winners: row, isRepeatedRow }, rowIndex) => (
          <div
            key={rowIndex}
            className={cx("winner-row", rowIndex % 2 === 1 && "is-reverse")}
            aria-hidden={isRepeatedRow || undefined}
          >
            {fillMarqueeRow(row).map(
              ({ winner, isDuplicate }, winnerIndex) => (
                <WinnerCard
                  key={`${winner.username}-${winnerIndex}`}
                  {...winner}
                  isDuplicate={isDuplicate || isRepeatedRow}
                />
              )
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
